import React, { useState, useMemo, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { formatCurrencySigFig } from '../../utils/formatters';
import type { RegistryComponentProps } from '../../pages/registry/componentRegistry';
import { getRegistryData } from '../../pages/registry/componentRegistry';
import { useFormContextOptional } from '../../contexts/FormContext';
import { usePageDataContextOptional } from '../../contexts/PageDataContext';
import Modal from '../common/Modal/Modal';
import { EconomicImpactChart } from '../FisheryCharts/FisheryCharts';
import InsightColumnPair from '../InsightColumnPair';
import './LandedEconomicImpact.css';

interface InsightData {
  economyValue?: number;
  economyUncertainty?: number;
}

interface LandedEconomicImpactData {
  insights?: InsightData;
  originalInsights?: InsightData;
  parametersChanged?: boolean;
  stockName?: string;
  onOpenEconomicImpactModal?: () => void;
}

function filterImpactByYear(impact: unknown[] | undefined, selectedYear: string): unknown[] {
  if (!impact || !Array.isArray(impact)) return [];
  const withYear = (item: unknown): number => (item as { year?: number })?.year ?? 0;
  if (selectedYear === 'historical') return impact.filter((item) => withYear(item) <= 2024);
  if (selectedYear === 'projections') return impact.filter((item) => withYear(item) >= 2025);
  return impact;
}

function isAllZero(data: unknown[], keys = ['actual', 'potential']): boolean {
  if (!data || !data.length) return true;
  return data.every((d) => {
    const obj = d as Record<string, unknown>;
    return keys.every((key) => (obj[key] === 0 || obj[key] === null || obj[key] === undefined));
  });
}

function getDescription(stockName?: string): string {
  const base = ', calculated as the ex-vessel value × multiplier (currently ≈ 4.6)';
  if (!stockName || stockName === 'All Stocks') {
    return `The total economic contribution generated by the fishery for all stocks${base}`;
  }
  return `The total economic contribution generated by the fishery for ${stockName}${base}`;
}

export default function LandedEconomicImpact(
  props: RegistryComponentProps
): React.ReactElement {
  const formContext = useFormContextOptional();
  const pageData = usePageDataContextOptional();
  const injectionData = getRegistryData<LandedEconomicImpactData>(props);

  const [expanded, setExpanded] = useState(false);
  const [showModal, setShowModal] = useState(false);

  const pageInsights = pageData?.data?.insights as InsightData | undefined;
  const pageOriginalInsights = pageData?.data?.originalInsights as InsightData | undefined;

  const pageStockName = pageData?.data?.stockName as string | undefined;
  const pageTimeSeriesData = pageData?.data?.timeSeriesData as { impact?: unknown[] } | undefined;
  const pageLoading = pageData?.loading as boolean | undefined;
  const pageError = pageData?.error as string | null | undefined;
  const selectedModel = pageData?.data?.selectedModel as string | undefined;

  const insights = injectionData.insights ?? pageInsights ?? {};
  const originalInsights = injectionData.originalInsights ?? pageOriginalInsights;
  const parametersChanged = (injectionData.parametersChanged ?? pageData?.data?.parametersChanged) ?? false;
  const stockName = injectionData.stockName ?? pageStockName;

  const selectedYear = (formContext?.values?.['time-period'] as string) ?? 'projections';
  const filteredImpact = useMemo(
    () => filterImpactByYear(pageTimeSeriesData?.impact, selectedYear),
    [pageTimeSeriesData?.impact, selectedYear]
  );
  const canOpenModal = selectedModel === 'WHAM';
  const onOpenModal = canOpenModal ? () => setShowModal(true) : undefined;

  const handleCloseModal = () => {
    setShowModal(false);
    document.body.style.overflow = 'auto';
  };

  // Listen for openEconomicImpactModal event (from MissedEconomicImpact, KeyInsights, etc.)
  useEffect(() => {
    const handler = () => setShowModal(true);
    window.addEventListener('openEconomicImpactModal', handler);
    return () => window.removeEventListener('openEconomicImpactModal', handler);
  }, []);

  const showSingleColumn = selectedYear === 'historical';
  const showStatusQuoColumn = !showSingleColumn;
  const showChangeIndicators =
    !showSingleColumn && !!originalInsights && parametersChanged;

  const statusQuoDisplay = originalInsights ?? insights;
  const description = getDescription(stockName);

  return (
    <div className="landed-economic-impact insight-card">
      <div className="insight-title-row">
        <h3 className="insight-title">Landed Economic Impact</h3>
        <button
          className="info-icon"
          onClick={() => setExpanded((e) => !e)}
          aria-label="Toggle description"
          type="button"
        >
          i
        </button>
      </div>
      <div className={`insight-subtitle-container ${expanded ? 'expanded' : ''}`}>
        <h2 className="insight-subtitle" dangerouslySetInnerHTML={{ __html: description }} />
      </div>
      <InsightColumnPair
        originalValue={originalInsights?.economyValue}
        currentValue={insights.economyValue}
        statusQuoContent={
          <>
            {formatCurrencySigFig(statusQuoDisplay.economyValue)}
            <div className="uncertainty-value">
              (± {formatCurrencySigFig(statusQuoDisplay.economyUncertainty, false)})
            </div>
          </>
        }
        currentContent={
          <>
            {formatCurrencySigFig(insights.economyValue)}
            <div className="uncertainty-value">
              (± {formatCurrencySigFig(insights.economyUncertainty, false)})
            </div>
          </>
        }
        showStatusQuoColumn={showStatusQuoColumn}
        showSingleColumn={showSingleColumn}
        showChangeIndicators={Boolean(showChangeIndicators)}
        parametersChanged={Boolean(parametersChanged)}
        onClick={onOpenModal}
        clickable={Boolean(onOpenModal)}
        isLoading={Boolean(pageLoading)}
        errorMessage={pageError}
      />

      {showModal &&
        createPortal(
          <Modal
            title={`ECONOMIC IMPACT: ${stockName ?? 'All Stocks'}`}
            onClose={handleCloseModal}
            size="extra-large"
            transparentBackdrop={true}
            parameterStyle={true}
            {...({ customMaxWidth: '90vw', customMaxHeight: '90vh' } as Record<string, unknown>)}
            bodyStyle={{ padding: '20px' }}
          >
            {pageLoading ? (
              <p className="noscroll-loading-message">Loading data...</p>
            ) : pageError ? (
              <p className="noscroll-error-message">{pageError}</p>
            ) : !filteredImpact.length || isAllZero(filteredImpact) ? (
              <p className="noscroll-no-data-message">Data deficient</p>
            ) : (
              <EconomicImpactChart data={filteredImpact} maxHeight={400} />
            )}
          </Modal>,
          document.body
        )}
    </div>
  );
}
